import { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

import { LabourerContext } from "../contexts/LabourerContext";

import SectionHeader from "./SectionHeader";
import Modal from "./Modal";

import LabourerLicenseTable from "./LabourerLicenseTable";
import UploadLicenseForm from "./UploadLicenseForm";
import ConfirmPrompt from "./ConfirmPrompt";
import LabourerDocumentTable from "./LabourerDocumentTable";
import UploadDocumentForm from "./UploadDocumentForm";
import ResumeSection from "./ResumeSection";

import licenseService from "../services/licenseService";

import useDocuments from "../hooks/useDocuments";
import useLicenses from "../hooks/useLicenses";
import documentService from "../services/documentService";

const LicensesAndDocuments = () => {
  const { labourer } = useContext(LabourerContext);

  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  const [isConfirmDeleteLicenceModalOpen, setIsConfirmDeleteLicenceModalOpen] = useState(false);
  const [isDeletingLicense, setIsDeletingLicense] = useState(false);

  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isUploadingLicense, setIsUploadingLicense] = useState(false);

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  const [isConfirmDeleteDocumentModalOpen, setIsConfirmDeleteDocumentModalOpen] = useState(false);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  const [licensesPage, setLicensesPage] = useState(1);
  const [licensesPerPage, setLicensesPerPage] = useState(10);

  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsPerPage, setDocumentsPerPage] = useState(10);

  const {
    data: licenseData,
    isLoading: isLoadingLicenses,
    mutate: mutateLicenses
  } = useLicenses({ labourerId: labourer._id, page: licensesPage, perPage: licensesPerPage });

  const licenses = licenseData?.licenses;
  const licenseCount = licenseData?.count;

  const {
    data: documentData,
    isLoading: isLoadingDocuments,
    mutate: mutateDocuments
  } = useDocuments({ labourerId: labourer._id, page: documentsPage, perPage: documentsPerPage });

  const documents = documentData?.documents;
  const documentCount = documentData?.count;

  const handleLicensesPageChange = page => {
    setLicensesPage(page);
  };

  const handleLicensesPerRowsChange = async (newPerPage, page) => {
    setLicensesPerPage(newPerPage);
    setLicensesPage(page);
  };

  const handleDocumentsPageChange = page => {
    setDocumentsPage(page);
  };

  const handleDocumentsPerRowsChange = async (newPerPage, page) => {
    setDocumentsPerPage(newPerPage);
    setDocumentsPage(page);
  };

  const handleCloseUpladLicenseModal = () => {
    setIsLicenseModalOpen(false);
    setSelectedLicense(null);
  }

  const handleCloseUploadDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
  }

  const handleExistingLicenseUpdateClicked = (license) => {
    setSelectedLicense(license);
    setIsLicenseModalOpen(true);
  }

  const handleExistingDocumentUpdateClicked = (document) => {
    setSelectedDocument(document);
    setIsDocumentModalOpen(true);
  }

  const handleUploadLicense = async (licenseData) => {
    if (isUploadingLicense) return;

    licenseData.labourer = labourer._id;

    try {
      setIsUploadingLicense(true);

      if (selectedLicense) {
        await licenseService.updateLicense({ licenseId: selectedLicense._id, licenseData });
      } else {
        await licenseService.createLicense({ licenseData });
      }

      await mutateLicenses();

      setIsLicenseModalOpen(false);

      toast.success('License uploaded');
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while uploading the license');
    } finally {
      setIsUploadingLicense(false);
    }
  };

  const handleUploadDocument = async (documentData) => {
    if (isUploadingDocument) return;

    documentData.labourer = labourer._id;

    try {
      setIsUploadingDocument(true);

      if (selectedDocument) {
        await documentService.updateDocument({ documentId: selectedDocument._id, documentData });
      } else {
        await documentService.createDocument({ documentData });
      }

      await mutateDocuments();

      setIsDocumentModalOpen(false);

      toast.success('Document uploaded');
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while uploading the document');
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const handleDeleteLicenseClicked = (license) => {
    setSelectedLicense(license);
    setIsConfirmDeleteLicenceModalOpen(true);
  }

  const handleDeleteDocumentClicked = (document) => {
    setSelectedDocument(document);
    setIsConfirmDeleteDocumentModalOpen(true);
  }

  const handleDeleteLicense = async () => {
    try {
      setIsDeletingLicense(true);

      await licenseService.deleteLicense({ licenseId: selectedLicense._id });
      await mutateLicenses();

      setIsDeletingLicense(false);
      setIsConfirmDeleteLicenceModalOpen(false);
      setSelectedLicense(null);

      toast.success('License deleted');
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while deleting the license');
    }
  }

  const handleDeleteDocument = async () => {
    try {
      setIsDeletingDocument(true);

      await documentService.deleteDocument({ documentId: selectedDocument._id });
      await mutateDocuments();

      setIsDeletingDocument(false);
      setIsConfirmDeleteDocumentModalOpen(false);
      setSelectedDocument(null);

      toast.success('Document deleted');
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while deleting the document');
    }
  }

  return (
    <>
      <Helmet>
        <title>Licenses & Documents | ASE Group</title>
      </Helmet>

      <div className="flex flex-col gap-4 md:gap-8">
      <div className="grid md:grid-cols-2 gap-4 md:gap-8">
        <div className="border-ase-blue border rounded-lg h-fit flex-grow pb-1">
          <SectionHeader
            title="Licenses"
            actionsLeft={
              <button
                onClick={() => setIsLicenseModalOpen(true)}
                className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
              >
                Add
              </button>
            }
          />

          <LabourerLicenseTable
            licenses={licenses}
            licenseCount={licenseCount}
            perPage={licensesPerPage}
            isLoading={isLoadingLicenses}
            onUpdateClicked={handleExistingLicenseUpdateClicked}
            onDeleteClicked={handleDeleteLicenseClicked}
            onPageChange={handleLicensesPageChange}
            onPerPageChange={handleLicensesPerRowsChange}
          />
        </div>

        <div className="border-ase-blue border rounded-lg h-fit flex-grow pb-1">
          <SectionHeader
            title="Documents"
            actionsLeft={
              <button
                onClick={() => setIsDocumentModalOpen(true)}
                className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
              >
                Add
              </button>
            }
          />

          <LabourerDocumentTable
            documents={documents}
            documentCount={documentCount}
            perPage={documentsPerPage}
            isLoading={isLoadingDocuments}
            onUpdateClicked={handleExistingDocumentUpdateClicked}
            onDeleteClicked={handleDeleteDocumentClicked}
            onPageChange={handleDocumentsPageChange}
            onPerPageChange={handleDocumentsPerRowsChange}
          />
        </div>
      </div>

      <ResumeSection />
      </div>

      <Modal isOpen={isLicenseModalOpen} overflowType="visible">
        <UploadLicenseForm
          license={selectedLicense}
          licenseType={selectedLicense?.type}
          disabledLicenseTypes={selectedLicense ? [] : licenses?.map(license => license.type._id)}
          isUploading={isUploadingLicense}
          onSubmit={handleUploadLicense}
          onCloseClicked={handleCloseUpladLicenseModal}
        />
      </Modal>

      <Modal isOpen={isDocumentModalOpen} overflowType="visible">
        <UploadDocumentForm
          document={selectedDocument}
          documentType={selectedDocument?.type}
          disabledDocumentTypes={selectedDocument ? [] : documents?.filter(doc => doc.type != null).map(doc => doc.type._id)}
          isUploading={isUploadingDocument}
          onSubmit={handleUploadDocument}
          onCloseClicked={handleCloseUploadDocumentModal}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteLicenceModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete your ${selectedLicense?.type?.hrwlCode} license?`}
          onConfirm={handleDeleteLicense}
          isLoading={isDeletingLicense}
          onCancel={() => setIsConfirmDeleteLicenceModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteDocumentModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete your ${selectedDocument?.documentType} document?`}
          onConfirm={handleDeleteDocument}
          isLoading={isDeletingDocument}
          onCancel={() => setIsConfirmDeleteDocumentModalOpen(false)}
        />
      </Modal>
    </>
  )
}

export default LicensesAndDocuments;
